import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, Briefcase, MapPin } from "lucide-react";
import { Container, GlassCard } from "@/components/site/Primitives";
import { ArticleDocBody } from "@/components/doc/ArticleDocBody";
import { loadCareerBySlug } from "@/lib/content-loader";
import { useFirmContact } from "@/lib/hooks/use-firm-contact";

export const Route = createFileRoute("/careers/$slug")({
  loader: async ({ params }) => {
    const job = await loadCareerBySlug(params.slug);
    if (!job) throw notFound();
    return { job };
  },
  head: ({ loaderData }) => {
    const job = loaderData?.job;
    const title = job ? `${job.title} — Careers — KS Lau & Co` : "Careers — KS Lau & Co";
    const desc = job?.excerpt || `Join KS Lau & Co as ${job?.title ?? "a team member"}.`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
      ],
    };
  },
  component: CareerDetailPage,
  notFoundComponent: () => (
    <div className="container-page py-40 text-center">
      <h1 className="text-2xl font-bold text-primary">Role not found</h1>
      <Link to="/careers" className="mt-4 inline-block text-base font-semibold text-primary hover:underline">
        ← All careers
      </Link>
    </div>
  ),
});

function CareerDetailPage() {
  const { job } = Route.useLoaderData();
  const firmContact = useFirmContact();
  const applyHref = job.applyEmail
    ? `mailto:${job.applyEmail}?subject=${encodeURIComponent(`Application — ${job.title}`)}`
    : `mailto:${firmContact.email}?subject=${encodeURIComponent(`Application — ${job.title}`)}`;

  return (
    <>
      <section className="relative isolate overflow-hidden pb-10 pt-16 md:pb-12 md:pt-20">
        <Container>
          <Link to="/careers" className="inline-flex items-center gap-2 text-base font-semibold text-primary hover:underline">
            <ArrowLeft className="h-4 w-4" /> All careers
          </Link>
          <div className="mt-6 max-w-3xl">
            <p className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-gold">
              <Briefcase className="h-3.5 w-3.5" /> Open role
            </p>
            <h1 className="mt-4 text-3xl font-semibold leading-tight tracking-tight text-primary md:text-5xl">
              {job.title}
            </h1>
            {job.excerpt ? (
              <p className="mt-5 text-lg leading-relaxed text-foreground/80">{job.excerpt}</p>
            ) : null}
            <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-base text-muted-foreground">
              {job.location ? (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="h-4 w-4" /> {job.location}
                </span>
              ) : null}
              {job.employmentType ? <span>{job.employmentType}</span> : null}
              {job.department ? <span>{job.department}</span> : null}
            </div>
          </div>
        </Container>
      </section>

      <section className="relative pb-20 md:pb-28">
        <Container>
          <article className="max-w-prose">
            <ArticleDocBody
              editorDoc={job.editorDoc}
              craftState={job.craftState}
              craftCustomCss={job.craftCustomCss}
            />
          </article>

          <div className="mx-auto mt-16 max-w-3xl">
            <GlassCard hover={false} className="p-8 md:p-10">
              <div className="grid items-center gap-5 md:grid-cols-[1.5fr_1fr]">
                <div>
                  <h2 className="text-xl font-bold tracking-tight text-primary">Ready to apply?</h2>
                  <p className="mt-2 text-base text-muted-foreground">
                    Send your CV and a brief note on why you would like to join KS Lau &amp; Co.
                  </p>
                </div>
                <a
                  href={applyHref}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-5 py-3 text-base font-semibold text-primary-foreground hover:bg-primary/90 md:justify-self-end"
                >
                  Apply now <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </GlassCard>
          </div>
        </Container>
      </section>
    </>
  );
}
